import { buttonStyle } from 'components/buttons/buttonStyle';
import { InfoOutlined } from 'components/icons';
import { ModalFooterSimple } from 'components/modals/ModalFooterSimple';
import { ModalHeader } from 'components/modals/ModalHeader';
import { SIMPLE_TABLE_ROW_CLASS } from 'components/tables/BasicTable';
import { Alert, AlertDescription, AlertTitle } from 'components/ui/alert';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { formatToHours } from 'components/util/HoursDisplay';
import MoneyDisplay from 'components/util/MoneyDisplay';
import {
    FOOTER_CELL_CLASS,
    NUMERIC_CELL_CLASS,
    NUMERIC_HEAD_CLASS,
    PAYER_CELL_CLASS,
    PAYER_HEAD_CLASS,
    PAYER_TABLE_CLASS,
    PAYER_TABLE_FRAME_CLASS,
} from 'pages/invoices/payers/PayerTableParts';
import { formatShareRate, shareAmountFromRate } from 'pages/invoices/payers/paymentShare';
import { Fragment, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { useGetInvoiceTimecardsAndExpensesQuery } from 'store/api/invoice-details';
import { useGetInvoiceTotalsQuery } from 'store/api/invoices';
import { InvoiceData } from 'types/invoices';
import { cn } from 'utils/cn';
import { getClassifierText, t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';
import { buildInvoiceSummary, RowCaption, SummaryCase, SummaryLine } from './invoiceSummaryTables';

const CLASSIFIER_CODES = ['EXPENSE_TYPE', 'INVOICE_ROW_TYPE', 'PRICING_LIMITATION_TYPE'];
const SECTION_TITLE = 'text-subtitle1 text-fg font-semibold';
const CAPTION = 'text-caption text-fg-secondary block font-normal';
// A read-only summary: the small simple table's 8px rows, so the dialog shows its figures without scrolling.
const CELL = cn(PAYER_CELL_CLASS, 'py-2');
const NUM_CELL = cn(NUMERIC_CELL_CLASS, 'py-2 tabular-nums');
const SUBTOTAL = 'border-border border-t font-semibold';
const FOOT = cn(FOOTER_CELL_CLASS, 'py-2.5');
const SHARE_HEAD = cn(NUMERIC_HEAD_CLASS, 'border-border border-l');
const SHARE_CELL = cn(NUM_CELL, 'border-border border-l font-semibold');
// Timecards that bill nothing: the frame is dashed, as the receipt's rules are, and the figures are secondary.
const INFO_FRAME = cn(PAYER_TABLE_FRAME_CLASS, 'border-dashed');
const HOURS_HEAD = cn(NUMERIC_HEAD_CLASS, 'w-[104px]');
const RATE_HEAD = cn(NUMERIC_HEAD_CLASS, 'w-[124px]');
const AMOUNT_HEAD = cn(NUMERIC_HEAD_CLASS, 'w-[140px]');

const TIMECARDS_TITLE = {
    fixed: 'invoices.invoice-summary.info.title',
    monthly: 'invoices.invoice-summary.info.recurring-title',
    copy: 'invoices.invoice-summary.info.copy-title',
};

interface Props {
    invoice: InvoiceData;
    /** A shared-payment copy, read as its receipt reads it: the whole invoice, and this payer's share beside it. */
    isShared: boolean;
    onClose: () => void;
}

/**
 * The invoice summary: per case, each person's billed hours, average rate and net amount, then the expenses on one
 * line and every other row, down to the invoice total. A case billed by a fee (a fixed or monthly fee, a copy) lists
 * its timecards in a second table, as not in the total. A joint invoice draws each case under a header row of its
 * own; a shared-payment copy adds the payer's share as a last column.
 */
export function InvoiceSummaryDialog({ invoice, isShared, onClose }: Props) {
    const { data: lines } = useGetInvoiceTimecardsAndExpensesQuery(invoice.id);
    const { data: totals } = useGetInvoiceTotalsQuery(invoice.id);
    const { classifiers } = useClassifiers({ codes: CLASSIFIER_CODES });
    const { displayDecimalScale } = useTimecardPrecision();
    const summary = buildInvoiceSummary({ invoice, isShared, totals, timecards: lines?.invoiceTimecards });
    const { cases, timecardCases, notice, share } = summary;

    const money = (amount: number, currency = summary.currency) => <MoneyDisplay amount={amount} currency={currency} />;
    const hours = (value: number) => formatToHours(value, displayDecimalScale);
    const rate = (amount: number, billedHours: number) => money(billedHours ? amount / billedHours : 0);
    const isJoint = cases.length > 1;
    const hasHourly = cases.some((matter) => matter.layout === 'hourly');
    const labelSpan = hasHourly ? 3 : 1;
    const columns = labelSpan + 1 + (share ? 1 : 0);
    // As the record header writes it: two decimals at most, trailing zeros dropped ("40 %", "33.33 %").
    const percent = share ? `${Number(formatShareRate(share.rate))} %` : '';
    const arrangementText = (matter: SummaryCase) =>
        getClassifierText(classifiers['PRICING_LIMITATION_TYPE'], matter.arrangement);
    const captionText = (caption: RowCaption) => {
        if (caption.type === 'other-fee') {
            return t('invoices.invoice-summary.other-fee');
        }
        if (caption.type === 'recurring') {
            return t('invoices.invoice-summary.recurring', { interval: caption.interval ?? '' });
        }
        return getClassifierText(classifiers['INVOICE_ROW_TYPE'], caption.code);
    };

    const caseHeader = (matter: SummaryCase, span: number) =>
        isJoint ? (
            <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                <TableCell colSpan={span} className={cn(CELL, 'bg-grey-50 border-border border-t pt-3')}>
                    <span className="flex items-center gap-2">
                        <span className="font-semibold">{matter.fullName}</span>
                        <Badge variant="outline">{arrangementText(matter)}</Badge>
                    </span>
                </TableCell>
            </TableRow>
        ) : null;
    const shareCell = (amount: number, className?: string) =>
        share ? (
            <TableCell className={cn(SHARE_CELL, className)}>
                {money(shareAmountFromRate(amount, share.rate))}
            </TableCell>
        ) : null;
    const label = (text: ReactNode, caption?: ReactNode) => (
        <div className="flex flex-col">
            <span className="font-semibold">{text}</span>
            {caption ? <span className={CAPTION}>{caption}</span> : null}
        </div>
    );
    const lineLabel = (line: SummaryLine) =>
        line.kind === 'expenses'
            ? label(
                  t('invoices.invoice-summary.expenses'),
                  line.expenseTypes.map((code) => getClassifierText(classifiers['EXPENSE_TYPE'], code)).join(', ')
              )
            : label(line.description, captionText(line.caption));

    return (
        <>
            <ModalHeader
                kit
                title={t('invoices.invoice-summary.title')}
                description={
                    summary.currency
                        ? t('invoices.invoice-summary.description', {
                              number: invoice.invoiceNumber ?? '',
                              payer: invoice.clientParty.name,
                              currency: summary.currency,
                          })
                        : undefined
                }
                onCancel={onClose}
            />
            <div className="flex min-h-0 flex-col gap-4 overflow-y-auto px-6 pt-4 pb-3 max-sm:px-4">
                {/* Until both land, the tables would draw zero figures with no currency. */}
                {totals && lines ? (
                    <>
                        {notice ? (
                            <Alert>
                                <InfoOutlined />
                                <AlertTitle>
                                    {t(
                                        notice.type === 'shared'
                                            ? 'invoices.invoice-summary.shared.title'
                                            : 'invoices.invoice-summary.copy.title',
                                        { position: String(notice.position), count: String(notice.count) }
                                    )}
                                </AlertTitle>
                                <AlertDescription>
                                    {notice.type === 'shared'
                                        ? t('invoices.invoice-summary.shared.text', {
                                              payer: invoice.clientParty.name,
                                              rate: percent,
                                          })
                                        : t('invoices.invoice-summary.copy.text')}
                                </AlertDescription>
                            </Alert>
                        ) : null}
                        {cases.map((matter) =>
                            matter.capCut ? (
                                <Alert key={matter.matterId}>
                                    <InfoOutlined />
                                    <AlertTitle>
                                        {isJoint
                                            ? t('invoices.invoice-summary.capped.title-case', { case: matter.fullName })
                                            : t('invoices.invoice-summary.capped.title')}
                                    </AlertTitle>
                                    <AlertDescription>
                                        <FormattedMessage
                                            id="invoices.invoice-summary.capped.text"
                                            values={{
                                                billed: money(matter.capCut.billed, matter.capCut.currency),
                                                value: money(matter.capCut.value, matter.capCut.currency),
                                                cut: money(matter.capCut.cut, matter.capCut.currency),
                                            }}
                                        />
                                    </AlertDescription>
                                </Alert>
                            ) : null
                        )}

                        <section className="flex flex-col gap-2">
                            {timecardCases.length ? (
                                <h3 className={SECTION_TITLE}>
                                    <FormattedMessage id="invoices.invoice-summary.billed" />
                                </h3>
                            ) : null}
                            <div className={PAYER_TABLE_FRAME_CLASS}>
                                <Table className={PAYER_TABLE_CLASS}>
                                    <TableHeader className="[&_tr]:border-b-0">
                                        <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                                            <TableHead className={PAYER_HEAD_CLASS}>
                                                {t('invoices.invoice-summary.item')}
                                            </TableHead>
                                            {hasHourly ? (
                                                <>
                                                    <TableHead className={HOURS_HEAD}>
                                                        {t('invoices.invoice-summary.hours')}
                                                    </TableHead>
                                                    <TableHead className={RATE_HEAD}>
                                                        {t('invoices.invoice-summary.avg-rate')}
                                                    </TableHead>
                                                </>
                                            ) : null}
                                            <TableHead className={AMOUNT_HEAD}>
                                                {t(
                                                    share
                                                        ? 'invoices.invoice-summary.whole-invoice'
                                                        : 'invoices.invoice-summary.net-amount'
                                                )}
                                            </TableHead>
                                            {share ? (
                                                <TableHead className={cn(SHARE_HEAD, 'w-[140px]')}>
                                                    <span className="text-fg block font-semibold">
                                                        {t('invoices.invoice-summary.share')}
                                                    </span>
                                                    <span className="block">{percent}</span>
                                                </TableHead>
                                            ) : null}
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {cases.map((matter) => (
                                            <Fragment key={matter.matterId}>
                                                {caseHeader(matter, columns)}
                                                {matter.layout === 'hourly' ? (
                                                    <>
                                                        {matter.people.map((person) => (
                                                            <TableRow
                                                                key={person.id}
                                                                className={SIMPLE_TABLE_ROW_CLASS}
                                                            >
                                                                <TableCell className={CELL}>
                                                                    {label(person.name)}
                                                                </TableCell>
                                                                <TableCell className={NUM_CELL}>
                                                                    {hours(person.hours)}
                                                                </TableCell>
                                                                <TableCell className={NUM_CELL}>
                                                                    {rate(person.amount, person.hours)}
                                                                </TableCell>
                                                                <TableCell className={NUM_CELL}>
                                                                    {money(person.amount)}
                                                                </TableCell>
                                                                {shareCell(person.amount)}
                                                            </TableRow>
                                                        ))}
                                                        <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                                                            <TableCell className={cn(CELL, SUBTOTAL)}>
                                                                <FormattedMessage
                                                                    id="invoices.invoice-summary.fees"
                                                                    values={{ count: matter.people.length }}
                                                                />
                                                            </TableCell>
                                                            <TableCell className={cn(NUM_CELL, SUBTOTAL)}>
                                                                {hours(matter.hours)}
                                                            </TableCell>
                                                            <TableCell className={cn(NUM_CELL, SUBTOTAL)}>
                                                                {rate(matter.fees, matter.hours)}
                                                            </TableCell>
                                                            <TableCell className={cn(NUM_CELL, SUBTOTAL)}>
                                                                {money(matter.fees)}
                                                            </TableCell>
                                                            {shareCell(matter.fees, 'border-t')}
                                                        </TableRow>
                                                    </>
                                                ) : null}
                                                {matter.lines.map((line) => (
                                                    <TableRow key={line.key} className={SIMPLE_TABLE_ROW_CLASS}>
                                                        <TableCell className={CELL} colSpan={labelSpan}>
                                                            {lineLabel(line)}
                                                        </TableCell>
                                                        <TableCell className={NUM_CELL}>{money(line.amount)}</TableCell>
                                                        {shareCell(line.amount)}
                                                    </TableRow>
                                                ))}
                                            </Fragment>
                                        ))}
                                    </TableBody>
                                    <TableFooter className="border-t-0 bg-transparent">
                                        <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                                            <TableCell className={FOOT} colSpan={labelSpan}>
                                                <span className="block">
                                                    {t('invoices.summary-sidebar.total-wo-vat')}
                                                </span>
                                                <span className={CAPTION}>
                                                    {t('invoices.summary-sidebar.total-w-vat')}
                                                </span>
                                            </TableCell>
                                            <TableCell className={cn(FOOT, 'text-right')}>
                                                <span className="block">
                                                    {money(summary.total?.totalWithoutVat ?? 0)}
                                                </span>
                                                <span className={CAPTION}>
                                                    {money(summary.total?.totalWithVat ?? 0)}
                                                </span>
                                            </TableCell>
                                            {share ? (
                                                <TableCell className={cn(FOOT, 'border-border border-l text-right')}>
                                                    <span className="block">
                                                        {money(share.total?.totalWithoutVat ?? 0)}
                                                    </span>
                                                    <span className={CAPTION}>
                                                        {money(share.total?.totalWithVat ?? 0)}
                                                    </span>
                                                </TableCell>
                                            ) : null}
                                        </TableRow>
                                    </TableFooter>
                                </Table>
                            </div>
                            {hasHourly ? (
                                <p className="text-caption text-fg-secondary">
                                    <FormattedMessage id="invoices.invoice-summary.avg-rate-note" />
                                </p>
                            ) : null}
                        </section>

                        {timecardCases.length ? (
                            <section className="flex flex-col gap-2">
                                <div className="flex items-center gap-2">
                                    <h3 className={SECTION_TITLE}>
                                        <FormattedMessage id={TIMECARDS_TITLE[summary.timecardsTitle]} />
                                    </h3>
                                    <Badge variant="outline">{t('invoices.invoice-summary.info.not-billed')}</Badge>
                                </div>
                                <div className={INFO_FRAME}>
                                    <Table className={PAYER_TABLE_CLASS}>
                                        <TableHeader className="[&_tr]:border-b-0">
                                            <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                                                <TableHead className={PAYER_HEAD_CLASS}>
                                                    {t('invoices.invoice-summary.employee')}
                                                </TableHead>
                                                <TableHead className={HOURS_HEAD}>
                                                    {t('invoices.invoice-summary.hours')}
                                                </TableHead>
                                                <TableHead className={RATE_HEAD}>
                                                    {t('invoices.invoice-summary.avg-rate')}
                                                </TableHead>
                                                <TableHead className={AMOUNT_HEAD}>
                                                    {t('invoices.invoice-summary.value-at-rates')}
                                                </TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody className="text-fg-secondary">
                                            {timecardCases.map((matter) => (
                                                <Fragment key={matter.matterId}>
                                                    {caseHeader(matter, 4)}
                                                    {matter.people.map((person) => (
                                                        <TableRow key={person.id} className={SIMPLE_TABLE_ROW_CLASS}>
                                                            <TableCell className={CELL}>{label(person.name)}</TableCell>
                                                            <TableCell className={NUM_CELL}>
                                                                {hours(person.hours)}
                                                            </TableCell>
                                                            <TableCell className={NUM_CELL}>
                                                                {rate(person.value, person.hours)}
                                                            </TableCell>
                                                            <TableCell className={NUM_CELL}>
                                                                {money(person.value)}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                    <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                                                        <TableCell className={cn(CELL, SUBTOTAL, 'border-dashed')}>
                                                            <FormattedMessage
                                                                id="invoices.invoice-summary.total-people"
                                                                values={{ count: matter.people.length }}
                                                            />
                                                        </TableCell>
                                                        <TableCell className={cn(NUM_CELL, SUBTOTAL, 'border-dashed')}>
                                                            {hours(matter.hours)}
                                                        </TableCell>
                                                        <TableCell className={cn(NUM_CELL, SUBTOTAL, 'border-dashed')}>
                                                            {rate(matter.value, matter.hours)}
                                                        </TableCell>
                                                        <TableCell className={cn(NUM_CELL, SUBTOTAL, 'border-dashed')}>
                                                            {money(matter.value)}
                                                        </TableCell>
                                                    </TableRow>
                                                </Fragment>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                                {summary.timecardsTitle === 'copy' ? (
                                    <p className="text-caption text-fg-secondary">
                                        <FormattedMessage id="invoices.invoice-summary.info.copy-note" />
                                    </p>
                                ) : null}
                            </section>
                        ) : null}
                    </>
                ) : (
                    <Skeleton className="h-48 w-full" />
                )}
            </div>
            <ModalFooterSimple
                kit
                buttons={[
                    <Button key="close" {...buttonStyle({ variant: 'outlined', color: 'primary' })} onClick={onClose}>
                        {t('close')}
                    </Button>,
                ]}
            />
        </>
    );
}
